import { listRiders } from "../../services/riders.service";
import { ALL_VALUE } from "./constants";

/** "All" is a UI sentinel, not a filter value. */
function realValue(value) {
  return value && value !== ALL_VALUE ? value : undefined;
}

/** Build rider list query params supported by GET /riders today. */
export function buildRiderListParams(filters = {}, extras = {}) {
  const params = {
    page: extras.page || 1,
    per_page: extras.per_page || 25,
    ...extras,
  };

  const project = realValue(filters.project);
  if (project) params.project = project;

  // Overall tab sends `zones` (multi-select); the other tabs still send `zone`.
  if (filters.zones?.length) params.zones = filters.zones.join(",");
  else if (realValue(filters.zone)) params.zone = filters.zone;

  if (filters.status) params.status = filters.status;

  // year/month/fleet/city/partners are not supported by riders list API yet
  delete params.year;
  delete params.month;
  delete params.fleetType;
  delete params.city;
  delete params.partners;

  return params;
}

export function extractPaginated(response) {
  return {
    records: response?.data?.records || [],
    pagination: response?.data?.pagination || {
      page: 1,
      per_page: 25,
      total_count: 0,
      total_pages: 0,
    },
  };
}

/**
 * Partner status counts for Overall → Partner Details.
 * Uses three lightweight count calls (per_page=1).
 */
export async function fetchPartnerStatusStats(filters = {}) {
  const base = buildRiderListParams(filters, { page: 1, per_page: 1 });

  // Active/inactive exclude soft-deleted; total is operational partners only
  const [activeRes, inactiveRes] = await Promise.all([
    listRiders({ ...base, status: "active" }),
    listRiders({ ...base, status: "inactive" }),
  ]);

  const active = extractPaginated(activeRes).pagination.total_count || 0;
  const inactive = extractPaginated(inactiveRes).pagination.total_count || 0;
  const total = active + inactive;
  const activePercent = total > 0 ? Math.round((active / total) * 100) : 0;

  return { total, active, inactive, activePercent };
}

/**
 * Shape a Dashboard API top-performer row for the panel.
 * Ranking (approved order count, deleted riders included) is computed server-side.
 */
export function mapPerformerRecord(row) {
  const name = row.fullName || "—";
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return {
    id: row._id,
    initials: initials || "—",
    name,
    riderId: row.riderId || "—",
    orders: Number(row.orders || 0),
    status: row.status,
    isDeleted: Boolean(row.isDeleted),
  };
}

/**
 * Partner dropdown options, cascaded by the current attribute filters.
 * city/fleetType are added here rather than in buildRiderListParams: the shared
 * builder still drops them because the other tabs filter those client-side.
 */
export async function fetchPartnerOptions(filters = {}) {
  const params = buildRiderListParams(filters, {
    page: 1,
    per_page: 100,
    sort_by: "fullName",
    sort_dir: "asc",
    status: "active",
  });

  const city = realValue(filters.city);
  if (city) params.city = city;
  const fleetType = realValue(filters.fleetType);
  if (fleetType) params.fleetType = fleetType;

  const response = await listRiders(params);
  const { records } = extractPaginated(response);
  return records.map((r) => ({
    label: `${r.riderId} — ${r.fullName}`,
    value: r._id,
  }));
}

export async function fetchPartnersInfoPage(filters = {}, page = 1, perPage = 25) {
  const response = await listRiders(
    buildRiderListParams(filters, {
      page,
      per_page: perPage,
      sort_by: "fullName",
      sort_dir: "asc",
    })
  );
  let { records, pagination } = extractPaginated(response);

  // Client-side filters not supported by riders API
  if (filters.fleetType) {
    records = records.filter(
      (r) => (r.fleetType?._id || r.fleetType) === filters.fleetType
    );
  }
  if (filters.city) {
    records = records.filter(
      (r) => (r.city?._id || r.city) === filters.city
    );
  }
  if (filters.partners?.length) {
    const set = new Set(filters.partners);
    records = records.filter((r) => set.has(r._id));
  }

  return {
    records: records.map(mapRiderToPartnersInfoRow),
    pagination,
  };
}

export function mapRiderToPartnersInfoRow(rider) {
  return {
    key: rider._id,
    partnerId: rider.riderId || "—",
    partnerName: rider.fullName || "—",
    fleetType: rider.fleetType?.name || "—",
    zone: rider.zone?.name || "—",
    nationality: rider.nationality?.name || "—",
    dateOfJoining: rider.dateOfJoining || null,
    status: rider.status || "inactive",
    daysWorked: "—",
  };
}
